import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  MapPin,
  MagnifyingGlass,
  Path,
  X,
  NavigationArrow,
} from "@phosphor-icons/react";

function pinIcon(color, n) {
  return L.divIcon({
    className: "",
    html: `<div class="brutal-pin" style="width:30px;height:30px;background:${color}">${n}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function FitBounds({ stops }) {
  const map = useMap();
  useEffect(() => {
    const pts = stops.filter((s) => s.lat && s.lng).map((s) => [s.lat, s.lng]);
    if (pts.length === 1) map.setView(pts[0], 13);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50] });
  }, [stops, map]);
  return null;
}

export default function Routing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [routes, setRoutes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newRoute, setNewRoute] = useState({ name: "", color: "#FEF08A" });
  const [address, setAddress] = useState("");
  const [searching, setSearching] = useState(false);

  const load = () => {
    api.get(`/routes`).then((r) => {
      setRoutes(r.data);
      if (r.data.length && !r.data.find((x) => x.id === activeId)) setActiveId(r.data[0].id);
      if (!r.data.length) setActiveId(null);
    });
  };
  useEffect(load, []);

  const active = useMemo(() => routes.find((r) => r.id === activeId), [routes, activeId]);

  const createRoute = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/routes", {
        name: newRoute.name,
        color: newRoute.color,
        date: "",
        worker_id: "",
        stops: [],
      });
      toast.success("Template saved");
      setCreating(false);
      setNewRoute({ name: "", color: "#FEF08A" });
      setActiveId(data.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const persist = async (route) => {
    await api.put(`/routes/${route.id}`, {
      name: route.name,
      date: route.date,
      worker_id: route.worker_id || "",
      color: route.color,
      stops: route.stops,
    });
    load();
  };

  const addStop = async (e) => {
    e.preventDefault();
    if (!active || !address.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/geocode?q=${encodeURIComponent(address)}`);
      if (!data.found) {
        toast.error("Address not found, try a more specific one");
        return;
      }
      const stop = {
        address: data.display_name,
        lat: data.lat,
        lng: data.lng,
        label: address,
        order: active.stops.length,
        done: false,
      };
      await persist({ ...active, stops: [...active.stops, stop] });
      setAddress("");
    } catch (e) {
      toast.error("Could not look up address");
    } finally {
      setSearching(false);
    }
  };

  const removeStop = async (idx) => {
    const stops = active.stops.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i }));
    await persist({ ...active, stops });
  };

  const toggleDone = async (idx) => {
    const stops = active.stops.map((s, i) => (i === idx ? { ...s, done: !s.done } : s));
    await persist({ ...active, stops });
  };

  const deleteRoute = async (id) => {
    await api.delete(`/routes/${id}`);
    toast.success("Route deleted");
    setActiveId(null);
    load();
  };

  const linePts = active?.stops.filter((s) => s.lat && s.lng).map((s) => [s.lat, s.lng]) || [];

  return (
    <div className="space-y-6" data-testid="routing-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Logistics</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Route templates</h1>
          <p className="text-base font-medium text-slate-600 mt-1">
            Build reusable routing plans here, then assign them to a shift in the Schedule.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreating(!creating)}
            data-testid="add-route-toggle"
            className={`bg-blue-500 text-white border-2 border-black rounded-lg px-5 py-2.5 font-bold brutal-shadow hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center gap-2 ${
              isAdmin ? "" : "hidden"
            }`}
          >
            <Plus weight="bold" size={20} /> New template
          </button>
        </div>
      </div>

      {creating && (
        <form
          onSubmit={createRoute}
          data-testid="route-form"
          className="bg-white border-2 border-black rounded-xl brutal-shadow p-5 grid sm:grid-cols-3 gap-4 items-end"
        >
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest">Template name</label>
            <input
              required
              data-testid="route-name"
              value={newRoute.name}
              onChange={(e) => setNewRoute({ ...newRoute, name: e.target.value })}
              className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none"
              placeholder="Downtown morning run"
            />
          </div>
          <div className="flex gap-2 items-end">
            <input
              type="color"
              value={newRoute.color}
              onChange={(e) => setNewRoute({ ...newRoute, color: e.target.value })}
              className="h-11 w-12 border-2 border-black rounded-lg"
            />
            <button
              type="submit"
              data-testid="route-save"
              className="flex-1 bg-emerald-300 border-2 border-black rounded-lg px-4 py-2.5 font-bold brutal-shadow-sm"
            >
              Save template
            </button>
          </div>
        </form>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* sidebar */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border-2 border-black rounded-xl brutal-shadow p-4">
            <h3 className="font-display text-lg font-bold mb-3">Saved templates</h3>
            {routes.length === 0 ? (
              <p className="text-sm font-medium text-slate-400 py-4 text-center">
                No templates yet. Create one to get started.
              </p>
            ) : (
              <div className="space-y-2">
                {routes.map((r) => {
                  return (
                    <button
                      key={r.id}
                      onClick={() => setActiveId(r.id)}
                      data-testid={`route-item-${r.id}`}
                      className={`w-full text-left border-2 border-black rounded-lg p-3 transition-all ${
                        activeId === r.id ? "brutal-shadow-sm -translate-y-0.5" : ""
                      }`}
                      style={{ backgroundColor: activeId === r.id ? r.color : "#fff" }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold">{r.name}</span>
                        <span className="text-xs font-bold bg-white border-2 border-black rounded px-2 py-0.5">
                          {r.stops.length} stops
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {active && (
            <div className="bg-white border-2 border-black rounded-xl brutal-shadow p-4" data-testid="active-route-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-bold flex items-center gap-2">
                  <Path weight="bold" size={18} /> {active.name}
                </h3>
                <button
                  onClick={() => deleteRoute(active.id)}
                  data-testid="delete-active-route"
                  className={`bg-red-300 border-2 border-black rounded-lg p-1.5 brutal-shadow-sm ${
                    isAdmin ? "" : "hidden"
                  }`}
                >
                  <Trash weight="bold" size={14} />
                </button>
              </div>

              <form onSubmit={addStop} className={`gap-2 mb-3 ${isAdmin ? "flex" : "hidden"}`}>
                <input
                  value={address}
                  data-testid="stop-address"
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Add address / place…"
                  className="flex-1 border-2 border-black rounded-lg px-3 py-2 font-medium text-sm focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={searching}
                  data-testid="stop-add"
                  className="bg-yellow-300 border-2 border-black rounded-lg px-3 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
                >
                  {searching ? "…" : <MagnifyingGlass weight="bold" size={18} />}
                </button>
              </form>

              {active.stops.length === 0 ? (
                <p className="text-sm font-medium text-slate-400 text-center py-3">
                  Add addresses to build the route.
                </p>
              ) : (
                <div className="space-y-2">
                  {active.stops.map((s, i) => (
                    <div
                      key={i}
                      data-testid={`stop-${i}`}
                      className="flex items-start gap-2 border-2 border-black rounded-lg p-2"
                    >
                      <div
                        className="w-6 h-6 shrink-0 rounded-full border-2 border-black flex items-center justify-center text-xs font-black mt-0.5"
                        style={{ backgroundColor: active.color }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-bold text-sm ${s.done ? "line-through text-slate-400" : ""}`}>
                          {s.label || s.address}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{s.address}</div>
                      </div>
                      <button
                        onClick={() => toggleDone(i)}
                        data-testid={`stop-done-${i}`}
                        className={`text-xs font-bold border-2 border-black rounded px-2 py-0.5 ${
                          s.done ? "bg-emerald-300" : "bg-white"
                        } ${isAdmin ? "" : "hidden"}`}
                      >
                        {s.done ? "Done" : "Mark"}
                      </button>
                      <button
                        onClick={() => removeStop(i)}
                        data-testid={`stop-remove-${i}`}
                        className={isAdmin ? "" : "hidden"}
                      >
                        <X weight="bold" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* map */}
        <div className="lg:col-span-7">
          <div className="bg-white border-2 border-black rounded-xl brutal-shadow overflow-hidden h-[600px]">
            <MapContainer
              center={[40.7128, -74.006]}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
              data-testid="map"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap"
              />
              {active && (
                <>
                  {linePts.length > 1 && (
                    <Polyline positions={linePts} pathOptions={{ color: "#000", weight: 4, dashArray: "8 8" }} />
                  )}
                  {active.stops.map((s, i) =>
                    s.lat && s.lng ? (
                      <Marker key={i} position={[s.lat, s.lng]} icon={pinIcon(active.color, i + 1)} />
                    ) : null
                  )}
                  <FitBounds stops={active.stops} />
                </>
              )}
            </MapContainer>
          </div>
          {!active && (
            <p className="mt-3 text-sm font-medium text-slate-500 flex items-center gap-2">
              <NavigationArrow weight="bold" size={16} /> Select or create a route, then add
              addresses to see them on the map.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
